import React, { useState, useEffect } from 'react'
import ThumbsUp from '../../assets/images/thumbsUp.svg'
import ThumbsUpSelected from '../../assets/images/thumbsUpSelected.svg'
import { Flex, ResponsiveSVG } from 'ui/acro'
import styled from 'styled-components'
import Text from 'ui/acro/typography/Text'
import commentAxios from 'api/commentsAxios'
import { class_series_like_and_dislike_events } from 'gtm/pushEvents'
import CoachmarkRenderer from './CoachMarkRenderer'

function normalizeLikes(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const n = Number(value)
  return Number.isFinite(n) ? n : 0
}

function normalizeLikedByMe(value) {
  if (value === true || value === 'true' || value === 1) return true
  if (value === false || value === 'false' || value === 0) return false
  return false
}

const LikeColumn = styled(Flex)`
  flex-direction: column;
  gap: 4px;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #000;
`
const IconSlot = styled.span`
  display: flex;
  width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 0;
  overflow: visible;

  svg {
    display: block;
    width: 26px;
    height: 26px;
    max-width: 100%;
    max-height: 100%;
  }
`
const LikeCount = styled(Text)({
  fontSize: ['14px'],
  fontFamily: 'Proxima-nova',
  fontWeight: 400,
  letterSpacing: '0.308px',
  minHeight: '18px',
  lineHeight: '14px',
  minWidth: '1ch',
  textAlign: 'center',
  width: '100%'
})

const LikesAndDislikes = ({
  type,
  classDetails,
  handleChange = () => {},
  coaches,
  username,
  createdAt,
  videoDetails,
  seriesDetails
}) => {
  const [likes, setLikes] = useState(0)
  const [userLiked, setUserLiked] = useState(false)
  const [load, setLoad] = useState(false)
  const [infoReady, setInfoReady] = useState(false)

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setInfoReady(true), 10000)
    fetchContentInfo().finally(() => window.clearTimeout(timeoutId))
    return () => window.clearTimeout(timeoutId)
  }, [])

  const fetchContentInfo = async () => {
    try {
      const response = await commentAxios.post('/content-info', {
        content_type: type,
        content_id: classDetails?.id
      })
      if (response.status === 200) {
        setUserLiked(normalizeLikedByMe(response?.data?.likedByMe))
        setLikes(normalizeLikes(response?.data?.likes))
      }
    } catch (err) {
      console.log(err)
      setUserLiked(false)
      setLikes(0)
    } finally {
      setInfoReady(true)
    }
  }

  useEffect(
    () => handleChange({ likes, userLiked }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [likes, userLiked]
  )

  const toggleLike = async () => {
    const prev = { userLiked, likes }
    try {
      setLoad(true)
      const response = await commentAxios.post(
        '/content-interaction/reaction',
        {
          content_type: type,
          content_id: classDetails?.id,
          type: 'LIKE',
          title: classDetails?.title,
          instructors: coaches?.map((coach) => ({
            ...coach,
            full_name: coach?.fullName
          }))
        }
      )
      if (response.status === 200) {
        setUserLiked(normalizeLikedByMe(response?.data?.likedByMe))
        setLikes(normalizeLikes(response?.data?.likes))
      } else {
        setUserLiked(prev.userLiked)
        setLikes(prev.likes)
      }
    } catch (err) {
      console.log(err)
      setUserLiked(prev.userLiked)
      setLikes(prev.likes)
    } finally {
      setLoad(false)
    }
  }

  const ga4LikeEvent = () => {
    try {
      const params = {
        contentType: type.toLowerCase(),
        eventType: 'up',
        instructor: coaches?.map((coach) => coach?.fullName).join(', '),
        style: classDetails?.style
      }
      if (type === 'CLASS') {
        class_series_like_and_dislike_events({
          ...params,
          seriesName: seriesDetails?.title,
          className: classDetails?.title,
          videoTitle: videoDetails?.title
        })
      } else if (type === 'SERIES') {
        class_series_like_and_dislike_events({
          ...params,
          seriesName: classDetails?.title
        })
      }
    } catch (e) {
      console.log(e)
    }
  }

  const likeClickHandler = () => {
    if (load || !infoReady) return
    const wasLiked = userLiked === true
    const nextLiked = !wasLiked
    setUserLiked(nextLiked)
    setLikes((c) => {
      const base = normalizeLikes(c)
      if (nextLiked && !wasLiked) return base + 1
      if (!nextLiked && wasLiked) return Math.max(0, base - 1)
      return base
    })
    toggleLike()
    ga4LikeEvent()
  }

  const count = normalizeLikes(likes)

  return (
    <>
      <LikeColumn
        data-pdp-like-control
        data-pdp-like-count={count}
        data-pdp-like-pressed={userLiked ? 'true' : 'false'}
        role='button'
        tabIndex={0}
        id='like-button'
        aria-pressed={Boolean(userLiked)}
        aria-label={`${count} likes${
          userLiked ? ', liked by you' : ''
        }. Press to toggle.`}
        style={{ cursor: load || !infoReady ? 'wait' : 'pointer' }}
        onClick={() => likeClickHandler()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            likeClickHandler()
          }
        }}
      >
        <IconSlot aria-hidden>
          <ResponsiveSVG
            SVG={userLiked ? ThumbsUpSelected : ThumbsUp}
            width='26px'
            height='26px'
          />
        </IconSlot>
        <LikeCount>{count}</LikeCount>
      </LikeColumn>
      {createdAt && (
        <CoachmarkRenderer
          page={type === 'CLASS' ? 'classDetails' : 'seriesDetails'}
          createdAt={createdAt}
          user={{ username, createdAt }}
        />
      )}
    </>
  )
}

export default LikesAndDislikes
